/**
 * Dispensa PERMANENTE (por navegador) de avisos de divergência (at-32).
 *
 * O "×" da faixa era estado de componente: sumia daquela renderização e voltava
 * no F5 — o consultor ficava com o aviso "na cara" até o TI resolver a fila, o
 * que não é operável (decisão de Victor, 01/09/2026). Agora o fechar grava os
 * IDS das divergências dispensadas, e a faixa filtra o que o usuário já viu.
 *
 * Por que por ID funciona: cada fato novo no SGA gera uma divergência NOVA
 * (id novo) — dispensar a atual não silencia mudanças futuras, que reaparecem
 * sozinhas. E a fila do TI não é afetada: a divergência continua ABERTA lá; o
 * consultor silencia a notificação, não o registro.
 *
 * Armazena só ids (uuid) — nunca PII. Limite de 300 para não crescer sem fim
 * (os mais antigos saem primeiro; se um dispensado antigo reaparecer, o
 * consultor o vê de novo uma vez — inofensivo).
 */

const CHAVE = 'saac:divergencias-dispensadas';
const LIMITE = 300;

const lerLista = (): string[] => {
  try {
    const cru = window.localStorage.getItem(CHAVE);
    const lista = cru ? JSON.parse(cru) : [];
    return Array.isArray(lista) ? lista.filter((v) => typeof v === 'string') : [];
  } catch {
    // localStorage indisponível/corrompido: degrada para "nada dispensado".
    return [];
  }
};

export const idsDispensados = (): Set<string> => new Set(lerLista());

export const dispensarDivergencias = (ids: string[]): void => {
  if (!ids.length) return;
  try {
    const atual = lerLista().filter((id) => !ids.includes(id));
    const nova = [...atual, ...ids].slice(-LIMITE);
    window.localStorage.setItem(CHAVE, JSON.stringify(nova));
  } catch {
    // sem storage, a dispensa vale só para a renderização atual — comportamento antigo.
  }
};

/**
 * Desfaz a dispensa dos ids informados e devolve **quantos** estavam dispensados
 * (at-54).
 *
 * ⚠️ Existe porque a dispensa silenciava até uma verificação PEDIDA pelo usuário.
 * O "×" foi feito para calar o aviso **passivo** — aquele que aparece sozinho ao
 * abrir a tela. Mas o botão "Verificar no SGA" é um ato deliberado: o consultor
 * clicava, o resultado vinha com divergência, a faixa se recusava a mostrá-la
 * (id já dispensado) e o caminho "tem divergência" não emite toast — o clique
 * ficava **completamente mudo**, indistinguível de "está tudo certo" e de "o
 * botão não funcionou" (relatado por Victor em 08/09/2026).
 *
 * Quem pede para ver, vê. A contagem devolvida é o que permite avisar na tela.
 */
export const reexibirDivergencias = (ids: string[]): number => {
  if (!ids.length) return 0;
  try {
    const atual = lerLista();
    const reexibidos = ids.filter((id) => atual.includes(id));
    if (!reexibidos.length) return 0;

    window.localStorage.setItem(
      CHAVE,
      JSON.stringify(atual.filter((id) => !reexibidos.includes(id))),
    );
    return reexibidos.length;
  } catch {
    // Sem storage não há dispensa gravada — nada a reexibir, e a faixa já mostra tudo.
    return 0;
  }
};
